/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { GLOBAL_APP_MENU_ID_TO_APS_KEY } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { fnGetFilteredProductLinks } from '@atlassian/global-shortcuts/types';
import {
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import {
	L1_MENU_ID,
	type GlobalAppMenuId,
	type JiraConfigurableNavigationItem,
	type MenuItemConfigData,
} from '../../types.tsx';
import { getUserNavigationItemLinks } from '../get-user-navigation-item-links/index.tsx';
import {
	hasGlobalAppLicense,
	isGlobalAppMenuId,
	isSupportedMenuId,
	type GlobalAppsLicense,
} from '../global-apps/index.tsx';

/**
 * Gets all the ConfigurableMenuIds that are NOT global apps for platform ingestion.
 * MenuIds and GlobalAppIdsare split because \@atlassian/customization-modal and
 * \@atlassian/global-more-menu take in separate props for apps/product items.
 */
export const getProductItemsData = (
	navItems: JiraConfigurableNavigationItem[],
): MenuItemConfigData[] =>
	navItems
		.filter((navItem) => isSupportedMenuId(navItem.menuId))
		.filter((navItem) => !isGlobalAppMenuId(navItem.menuId))
		.filter((navItem) => navItem.menuId !== L1_MENU_ID.TEAMS)
		.filter((navItem) => {
			if (!fg('itam_ftw')) {
				return navItem.menuId !== L1_MENU_ID.ITAM;
			}
			return true;
		})
		.map((item) => {
			return {
				menuId: item.menuId,
				visible: item.isVisible,
			};
		});

/**
 * Gets only GlobalAppMenuIds from ConfigurableMenuIds for platform ingestion.
 * MenuIds and GlobalAppIds are split because \@atlassian/customization-modal and
 * \@atlassian/global-more-menu take in separate props for apps/product items.
 */
export const getAppItemsData = (
	navItems: JiraConfigurableNavigationItem[],
	globalAppLicense: GlobalAppsLicense,
	getFilteredProductLinks?: fnGetFilteredProductLinks,
): MenuItemConfigData[] => {
	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

	return navItems
		.filter((navItem) => isGlobalAppMenuId(navItem.menuId))
		.filter((navItem) => {
			// Metadata app licencing check
			if (!isProductSupportedFromMetadata(navItem.menuId)) {
				return false;
			}
			if (getFilteredProductLinks) {
				if (navItem.menuId in GLOBAL_APP_MENU_ID_TO_APS_KEY) {
					const apsKey = GLOBAL_APP_MENU_ID_TO_APS_KEY[navItem.menuId];
					const fallbackUrl = USER_NAVIGATION_ITEM_LINKS[navItem.menuId];

					const filteredProducts = getFilteredProductLinks(apsKey);

					const statusDisabledOrComplete = filteredProducts.status === 'complete';

					if (statusDisabledOrComplete && !fallbackUrl && filteredProducts.links.length === 0) {
						// If there are no filtered links, we don't want to show this item.
						return false;
					}
				}
			}

			return true;
		})
		.filter((navItem) => hasGlobalAppLicense(navItem.menuId, globalAppLicense))
		.map((item) => {
			return {
				menuId: item.menuId,
				visible: item.isVisible,
			};
		});
};

/**
 * Returns all the currently visible global apps.
 */
export const getVisibleAppItemsData = (
	navItems: JiraConfigurableNavigationItem[],
	globalAppLicense: GlobalAppsLicense,
	getFilteredProductLinks?: fnGetFilteredProductLinks,
): GlobalAppMenuId[] => {
	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

	return navItems
		.filter((navItem) => navItem.isVisible)
		.map(({ menuId }) => menuId)
		.filter(isGlobalAppMenuId)
		.filter((navItem) => {
			// Metadata app licencing check
			if (!isProductSupportedFromMetadata(navItem)) {
				return false;
			}
			if (getFilteredProductLinks) {
				if (navItem in GLOBAL_APP_MENU_ID_TO_APS_KEY) {
					const apsKey = GLOBAL_APP_MENU_ID_TO_APS_KEY[navItem];
					const filteredProducts = getFilteredProductLinks(apsKey);
					const fallbackUrl = USER_NAVIGATION_ITEM_LINKS[navItem];

					const statusDisabledOrComplete = filteredProducts.status === 'complete';

					if (statusDisabledOrComplete && !fallbackUrl && filteredProducts.links.length === 0) {
						// If there are no filtered links, we don't want to show this item.
						return false;
					}
				}
			}

			return true;
		})
		.filter((menuId) => hasGlobalAppLicense(menuId, globalAppLicense));
};

const isProductSupportedFromMetadata = (menuId: string) => {
	const products = getLicensedProducts();
	switch (menuId) {
		case L1_MENU_ID.GLOBAL_APP_CONFLUENCE:
			return !!products[LICENSED_PRODUCTS.CONFLUENCE];
		case L1_MENU_ID.GLOBAL_APP_LOOM:
			return !!products[LICENSED_PRODUCTS.LOOM];
		default:
			return true;
	}
};
